import React, { useMemo, useState, useEffect } from 'react';
import { Trash2, ClipboardList, CalendarDays, Coffee, Layers, Download, Upload, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DeleteModal from '../Common/DeleteModal';
import ExportModal from './ExportModal';
import ImportModal from './ImportModal';
import { useNotes } from '../../contexts/NotesContext';
import { useCalendar } from '../../contexts/CalendarContext';
import { useBreakReminder } from '../../contexts/BreakReminderContext';
import { useAuth } from '../../contexts/AuthContext';
import { notesApi, calendarsApi, eventsApi, breaksApi } from '../../services/cloudApi';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const ACTION_ICONS = {
  all: Layers,
  notes: ClipboardList,
  calendar: CalendarDays,
  breaks: Coffee,
};

const DataSettingsSection = () => {
  const { t, i18n } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const { user, isAuthenticated, authToken } = useAuth();
  const { notes, clearAllNotes, reloadNotes } = useNotes();
  const { allCalendars, allEvents, clearAllCalendarData, reloadCalendarsAndEvents } = useCalendar();
  const { breaks, clearAllBreakReminders, reloadBreaks } = useBreakReminder();

  const [pendingAction, setPendingAction] = useState(null);
  const [isProcessing, setProcessing] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    phase: 'idle',
    percent: 0,
    message: '',
  });
  const [pendingImportData, setPendingImportData] = useState(null);

  const ACTION_CONFIG = useMemo(() => ({
    all: {
      icon: ACTION_ICONS.all,
      title: t('dataSection.actions.all.title'),
      description: t('dataSection.actions.all.description'),
      confirmMessage: t('dataSection.actions.all.confirmMessage'),
    },
    notes: {
      icon: ACTION_ICONS.notes,
      title: t('dataSection.actions.notes.title'),
      description: t('dataSection.actions.notes.description'),
      confirmMessage: t('dataSection.actions.notes.confirmMessage'),
    },
    calendar: {
      icon: ACTION_ICONS.calendar,
      title: t('dataSection.actions.calendar.title'),
      description: t('dataSection.actions.calendar.description'),
      confirmMessage: t('dataSection.actions.calendar.confirmMessage'),
    },
    breaks: {
      icon: ACTION_ICONS.breaks,
      title: t('dataSection.actions.breaks.title'),
      description: t('dataSection.actions.breaks.description'),
      confirmMessage: t('dataSection.actions.breaks.confirmMessage'),
    },
  }), [t]);

  useEffect(() => {
    if (importProgress.phase !== 'done') return;
    window.dispatchEvent(
      new CustomEvent('notesplus-show-toast', {
        detail: {
          type: 'success',
          title: t('dataSection.toasts.dataImported'),
          message: '',
        },
      })
    );
    setImportProgress({ phase: 'idle', percent: 0, message: '' });
    setIsImporting(false);
    setPendingImportData(null);
  }, [importProgress.phase, t]);

  const normalizeTimestampValue = (value) => {
    if (!value) {
      return new Date().toISOString();
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return new Date().toISOString();
      }

      if (/^\d+$/.test(trimmed)) {
        const numeric = Number(trimmed);
        if (Number.isFinite(numeric)) {
          const date = new Date(numeric);
          if (!Number.isNaN(date.getTime())) {
            return date.toISOString();
          }
        }
      }

      const parsed = new Date(trimmed);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString();
      }
    }

    return new Date().toISOString();
  };

  const safeParseJson = (value, fallback) => {
    if (typeof value !== 'string') {
      return fallback;
    }

    try {
      const parsed = JSON.parse(value);
      return parsed;
    } catch (error) {
      console.warn('JSON parse hatası:', error);
      return fallback;
    }
  };

  const DEFAULT_BREAK_DAYS = {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  };

  // Export: Seçilen verileri JSON'a yaz
  const handleExportConfirm = async (selectedData) => {
    if (isExporting) return;

    try {
      setIsExporting(true);
      const timestamp = new Date().toISOString();

      const exportPayload = {
        meta: {
          version: 1,
          exportedAt: timestamp,
        },
        exported: {
          notes: selectedData.notes || false,
          calendars: selectedData.calendars || false,
          breaks: selectedData.breaks || false,
        },
        data: {},
      };

      // Notes
      if (selectedData.notes && Array.isArray(notes)) {
        exportPayload.data.notes = notes.map((note) => ({
          title: typeof note?.title === 'string' ? note.title : '',
          content: typeof note?.content === 'string' ? note.content : '',
          titleColor: note?.titleColor || note?.title_color || '#ffffff',
          createdAt: normalizeTimestampValue(note?.createdAt || note?.created_at),
          updatedAt: normalizeTimestampValue(
            note?.updatedAt || note?.updated_at || note?.createdAt || note?.created_at
          ),
        }));
      }

      // Calendars (Özel Günler hariç)
      if (selectedData.calendars && Array.isArray(allCalendars)) {
        // "Özel Günler" takvimini filtrele
        const filteredCalendars = allCalendars.filter(
          (cal) => cal?.name?.toLowerCase() !== 'özel günler' && cal?.name?.toLowerCase() !== 'ozel gunler'
        );

        // Calendar'ları export et (geçici index ID'leri ile)
        const calendarIndexMap = {}; // Calendar ID -> Export index mapping
        exportPayload.data.calendars = filteredCalendars.map((calendar, index) => {
          const tempId = `temp_cal_${index}`;
          calendarIndexMap[calendar.id] = tempId;
          return {
            tempId, // Geçici ID (import'ta mapping için)
            name: typeof calendar?.name === 'string' ? calendar.name : i18n.t('calendar:defaults.calendar'),
            color: calendar?.color || '#ffffff',
            description: typeof calendar?.description === 'string' ? calendar.description : '',
            createdAt: normalizeTimestampValue(calendar?.createdAt || calendar?.created_at),
            updatedAt: normalizeTimestampValue(
              calendar?.updatedAt || calendar?.updated_at || calendar?.createdAt || calendar?.created_at
            ),
          };
        });

        // Events (sadece export edilen takvimlere ait)
        const exportedCalendarIds = new Set(filteredCalendars.map((cal) => cal.id));
        const filteredEvents = Array.isArray(allEvents)
          ? allEvents.filter(
              (event) =>
                event?.calendarId &&
                exportedCalendarIds.has(event.calendarId) &&
                !event?.isSpecialDay // Özel gün event'lerini hariç tut
            )
          : [];

        exportPayload.data.events = filteredEvents.map((event) => ({
          calendarTempId: calendarIndexMap[event.calendarId] || null, // Geçici ID kullan
          title: typeof event?.title === 'string' ? event.title : '',
          description: typeof event?.description === 'string' ? event.description : '',
          startDate: event?.startDate ?? event?.start_date ?? null,
          endDate: event?.endDate ?? event?.end_date ?? event?.startDate ?? event?.start_date ?? null,
          startTime: event?.startTime ?? event?.start_time ?? null,
          endTime: event?.endTime ?? event?.end_time ?? null,
          allDay: Boolean(event?.allDay ?? event?.all_day),
          reminderDay: event?.reminderDay ?? event?.reminder_day ?? null,
          reminderHour: event?.reminderHour ?? event?.reminder_hour ?? null,
          repeatType: event?.repeatType ?? event?.repeat_type ?? 'none',
          createdAt: normalizeTimestampValue(event?.createdAt || event?.created_at),
          updatedAt: normalizeTimestampValue(
            event?.updatedAt ?? event?.updated_at ?? event?.createdAt ?? event?.created_at
          ),
        }));
      } else {
        exportPayload.data.events = [];
      }

      // Breaks
      if (selectedData.breaks && Array.isArray(breaks)) {
        exportPayload.data.breaks = breaks.map((breakItem) => ({
          name: typeof breakItem?.name === 'string' ? breakItem.name : i18n.t('breaks:defaults.break'),
          startTime: breakItem?.startTime ?? breakItem?.start_time ?? '10:00',
          durationMinutes: Number.isFinite(Number(breakItem?.durationMinutes ?? breakItem?.duration_minutes))
            ? Number(breakItem?.durationMinutes ?? breakItem?.duration_minutes)
            : 15,
          activeDays:
            breakItem?.activeDays ??
            safeParseJson(breakItem?.active_days, { ...DEFAULT_BREAK_DAYS }) ??
            { ...DEFAULT_BREAK_DAYS },
          soundEnabled:
            typeof breakItem?.soundEnabled === 'boolean'
              ? breakItem.soundEnabled
              : typeof breakItem?.sound_enabled === 'boolean'
              ? breakItem.sound_enabled
              : breakItem?.sound_enabled === 1,
          createdAt: normalizeTimestampValue(breakItem?.createdAt || breakItem?.created_at),
          updatedAt: normalizeTimestampValue(
            breakItem?.updatedAt ?? breakItem?.updated_at ?? breakItem?.createdAt ?? breakItem?.created_at
          ),
        }));
      }

      const content = JSON.stringify(exportPayload, null, 2);
      const suggestedFileName = `notesplus-export-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;

      let exported = false;
      if (typeof window !== 'undefined' && window.api?.exportDataToFile) {
        try {
          const result = await window.api.exportDataToFile({
            content,
            suggestedFileName,
          });

          if (result?.success) {
            exported = true;
          } else if (result?.error === 'USER_CANCELLED') {
            return;
          }
        } catch (ipcError) {
          console.warn('IPC üzerinden dışa aktarma başarısız:', ipcError);
        }
      }

      if (!exported) {
        // Browser fallback
        const blob = new Blob([content], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = suggestedFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        exported = true;
      }

      if (exported) {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: t('dataSection.toasts.exportedSuccess'),
              message: '',
            },
          })
        );
      }

      setShowExportModal(false);
    } catch (error) {
      console.error('Veri dışa aktarma hatası:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('dataSection.toasts.exportErrorTitle'),
            message: getApiErrorMessage(error, { fallback: t('dataSection.toasts.exportErrorGeneric') }),
          },
        })
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Import: Dosya seç ve modal aç
  const handleImportData = async () => {
    if (isImporting) return;

    try {
      setIsImporting(true);
      setImportProgress({ phase: 'file', percent: 5, message: t('dataSection.importFlow.selectingFile') });
      let fileContent = null;

      if (typeof window !== 'undefined' && window.api?.importDataFromFile) {
        try {
          const result = await window.api.importDataFromFile();
          if (result?.success && typeof result?.content === 'string') {
            fileContent = result.content;
            setImportProgress({ phase: 'file', percent: 15, message: t('dataSection.importFlow.readingFile') });
          } else if (result?.error === 'USER_CANCELLED') {
            return;
          }
        } catch (ipcError) {
          console.warn('IPC üzerinden içe aktarma başarısız:', ipcError);
        }
      }

      if (!fileContent) {
        // Browser fallback
        fileContent = await new Promise((resolve, reject) => {
          try {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json,application/json';
            input.style.display = 'none';

            input.onchange = () => {
              const file = input.files?.[0];
              if (!file) {
                resolve(null);
                return;
              }

              const reader = new FileReader();
              reader.onload = () => {
                if (typeof reader.result === 'string') {
                  resolve(reader.result);
                } else {
                  resolve(null);
                }
                document.body.removeChild(input);
              };
              reader.onerror = () => {
                document.body.removeChild(input);
                reject(new Error(t('dataSection.importFlow.fileReadError')));
              };
              reader.readAsText(file, 'utf-8');
            };

            input.oncancel = () => {
              document.body.removeChild(input);
              resolve(null);
            };

            document.body.appendChild(input);
            input.click();
          } catch (error) {
            reject(error);
          }
        });
      }

      if (!fileContent) {
        return;
      }

      setImportProgress({ phase: 'file', percent: 20, message: t('dataSection.importFlow.validatingBackup') });

      let parsed;
      try {
        parsed = JSON.parse(fileContent);
      } catch (error) {
        throw new Error(t('dataSection.importFlow.invalidFile'));
      }

      if (!parsed || typeof parsed !== 'object' || !parsed.meta || !parsed.data) {
        throw new Error(t('dataSection.importFlow.invalidOrMissing'));
      }

      setPendingImportData(parsed);
      setShowImportModal(true);
    } catch (error) {
      console.error('❌ [Import] Veri içe aktarma hatası:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('dataSection.toasts.importErrorTitle'),
            message: getApiErrorMessage(error, { fallback: t('dataSection.toasts.importErrorGeneric') }),
          },
        })
      );
    } finally {
      setIsImporting(false);
      setImportProgress({ phase: 'idle', percent: 0, message: '' });
    }
  };

  // Import: Seçilen verileri PostgreSQL'e yaz — phase-based progress, toast sadece "done" sonrası
  const handleImportConfirm = async ({ selectedData, overwrite }) => {
    if (!pendingImportData || !isAuthenticated || !authToken) {
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('dataSection.toasts.importErrorTitle'),
            message: t('dataSection.toasts.loginRequired'),
          },
        })
      );
      return;
    }

    setShowImportModal(false);
    setIsImporting(true);
    const exportData = pendingImportData.data || {};

    try {
      // Overwrite: Seçilen veri tiplerini toplu sil
      if (overwrite) {
        setImportProgress({
          phase: 'clearing',
          percent: 5,
          message: t('dataSection.importFlow.clearingExisting'),
        });

        if (selectedData.notes) {
          await notesApi.deleteAll({ token: authToken });
          await clearAllNotes();
          console.log('✅ [Import] Tüm notlar silindi');
        }
        if (selectedData.calendars) {
          await eventsApi.deleteAll({ token: authToken });
          await calendarsApi.deleteAll({ token: authToken });
          await clearAllCalendarData();
          console.log('✅ [Import] Tüm takvimler ve etkinlikler silindi');
        }
        if (selectedData.breaks) {
          await breaksApi.deleteAll({ token: authToken });
          await clearAllBreakReminders();
          console.log('✅ [Import] Tüm molalar silindi');
        }
      }

      // Notes: PostgreSQL'e yaz
      if (selectedData.notes && Array.isArray(exportData.notes) && exportData.notes.length > 0) {
        setImportProgress({
          phase: 'notes',
          percent: 10,
          message: t('dataSection.importFlow.writingNotes'),
        });

        const notesToInsert = exportData.notes.map((note) => ({
          title: note.title || tCommon('unnamed'),
          content: note.content || '',
          titleColor: note.titleColor || '#ffffff',
          createdAt: normalizeTimestampValue(note.createdAt),
          updatedAt: normalizeTimestampValue(note.updatedAt || note.createdAt),
        }));

        await notesApi.upsert({ token: authToken, notes: notesToInsert });
        console.log(`✅ [Import] ${notesToInsert.length} not PostgreSQL'e yazıldı`);
      }

      // Calendars: PostgreSQL'e yaz
      let calendarIdMap = {};
      if (selectedData.calendars && Array.isArray(exportData.calendars) && exportData.calendars.length > 0) {
        setImportProgress({
          phase: 'calendars',
          percent: 40,
          message: t('dataSection.importFlow.writingCalendars'),
        });

        const calendarsToInsert = exportData.calendars.map((calendar) => ({
          name: calendar.name || t('dataSection.importFlow.defaultCalendarName'),
          color: calendar.color || '#ffffff',
          description: calendar.description || '',
          createdAt: normalizeTimestampValue(calendar.createdAt),
          updatedAt: normalizeTimestampValue(calendar.updatedAt || calendar.createdAt),
        }));

        const result = await calendarsApi.upsert({ token: authToken, calendars: calendarsToInsert });
        if (Array.isArray(result?.calendars)) {
          exportData.calendars.forEach((oldCal, index) => {
            if (result.calendars[index]?.id && oldCal.tempId) {
              calendarIdMap[oldCal.tempId] = result.calendars[index].id;
            }
          });
        }
        console.log(`✅ [Import] ${calendarsToInsert.length} takvim PostgreSQL'e yazıldı`);
      }

      // Events: PostgreSQL'e yaz
      if (selectedData.calendars && Array.isArray(exportData.events) && exportData.events.length > 0) {
        setImportProgress({
          phase: 'calendars',
          percent: 50,
          message: t('dataSection.importFlow.writingEvents'),
        });

        const eventsToInsert = exportData.events
          .map((event) => {
            const newCalendarId = event.calendarTempId ? calendarIdMap[event.calendarTempId] : null;
            if (!newCalendarId) return null;
            return {
              calendarId: newCalendarId,
              title: event.title || '',
              description: event.description || '',
              startDate: event.startDate,
              endDate: event.endDate || event.startDate,
              startTime: event.startTime,
              endTime: event.endTime,
              allDay: Boolean(event.allDay),
              reminderDay: event.reminderDay,
              reminderHour: event.reminderHour,
              repeatType: event.repeatType || 'none',
              createdAt: normalizeTimestampValue(event.createdAt),
              updatedAt: normalizeTimestampValue(event.updatedAt || event.createdAt),
            };
          })
          .filter(Boolean);

        if (eventsToInsert.length > 0) {
          await eventsApi.upsert({ token: authToken, events: eventsToInsert });
          console.log(`✅ [Import] ${eventsToInsert.length} etkinlik PostgreSQL'e yazıldı`);
        }
      }

      // Breaks: PostgreSQL'e yaz
      if (selectedData.breaks && Array.isArray(exportData.breaks) && exportData.breaks.length > 0) {
        setImportProgress({
          phase: 'breaks',
          percent: 65,
          message: t('dataSection.importFlow.writingBreaks'),
        });

        const breaksToInsert = exportData.breaks.map((breakItem) => {
          let activeDays = breakItem.activeDays;
          if (!activeDays && typeof breakItem.active_days === 'string') {
            activeDays = safeParseJson(breakItem.active_days, { ...DEFAULT_BREAK_DAYS });
          } else if (!activeDays && typeof breakItem.active_days === 'object' && breakItem.active_days !== null) {
            activeDays = { ...DEFAULT_BREAK_DAYS, ...breakItem.active_days };
          }
          if (!activeDays || typeof activeDays !== 'object') {
            activeDays = { ...DEFAULT_BREAK_DAYS };
          }
          return {
            name: breakItem.name || t('dataSection.importFlow.defaultBreakName'),
            startTime: breakItem.startTime || '10:00',
            durationMinutes: Number.isFinite(Number(breakItem.durationMinutes))
              ? Number(breakItem.durationMinutes)
              : 15,
            activeDays,
            soundEnabled:
              typeof breakItem.soundEnabled === 'boolean'
                ? breakItem.soundEnabled
                : breakItem.sound_enabled === 1 || breakItem.sound_enabled === true,
            createdAt: normalizeTimestampValue(breakItem.createdAt),
            updatedAt: normalizeTimestampValue(breakItem.updatedAt || breakItem.createdAt),
          };
        });

        await breaksApi.upsert({ token: authToken, breaks: breaksToInsert });
        console.log(`✅ [Import] ${breaksToInsert.length} mola PostgreSQL'e yazıldı`);
      }

      // Reload: Context'leri API'den yeniden yükle — anlık senkron, sayfa yenileme yok
      setImportProgress({
        phase: 'reloading',
        percent: 85,
        message: t('dataSection.importFlow.loadingData'),
      });

      if (selectedData.notes && reloadNotes) {
        await reloadNotes();
      }
      if (selectedData.calendars && reloadCalendarsAndEvents) {
        await reloadCalendarsAndEvents();
      }
      if (selectedData.breaks && reloadBreaks) {
        await reloadBreaks();
      }

      setImportProgress({
        phase: 'done',
        percent: 100,
        message: t('dataSection.importFlow.done'),
      });
    } catch (error) {
      console.error('❌ [Import] Veri içe aktarma hatası:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('dataSection.toasts.importErrorTitle'),
            message: getApiErrorMessage(error, { fallback: t('dataSection.toasts.importErrorGeneric') }),
          },
        })
      );
      setIsImporting(false);
      setImportProgress({ phase: 'idle', percent: 0, message: '' });
      setPendingImportData(null);
    }
  };

  const handleAction = async (action) => {
    if (!action) return;
    setProcessing(true);

    try {
      let successTitle = '';
      if (action === 'all') {
        await Promise.all([clearAllNotes(), clearAllCalendarData(), clearAllBreakReminders()]);
        successTitle = t('dataSection.toasts.allClearedSuccess');
      } else if (action === 'notes') {
        await clearAllNotes();
        successTitle = t('dataSection.toasts.notesClearedSuccess');
      } else if (action === 'calendar') {
        await clearAllCalendarData();
        successTitle = t('dataSection.toasts.calendarClearedSuccess');
      } else if (action === 'breaks') {
        await clearAllBreakReminders();
        successTitle = t('dataSection.toasts.breaksClearedSuccess');
      }
      if (successTitle && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: {
              type: 'success',
              title: successTitle,
              message: '',
            },
          })
        );
      }
    } catch (error) {
      console.error('Veri temizleme hatası:', error);
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('dataSection.toasts.clearErrorTitle'),
            message: getApiErrorMessage(error, { fallback: t('dataSection.toasts.clearErrorGeneric') }),
          },
        })
      );
    } finally {
      setProcessing(false);
      setPendingAction(null);
    }
  };

  const isBusy = isProcessing || isExporting || isImporting;

  const renderCard = (actionKey) => {
    const config = ACTION_CONFIG[actionKey];
    const Icon = config.icon;
    const isDanger = actionKey === 'all';

    return (
      <button
        key={actionKey}
        type="button"
        onClick={() => setPendingAction(actionKey)}
        disabled={isBusy}
        className={`flex items-center justify-between w-full p-4 text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed border-b border-slate-100 dark:border-slate-800/50 last:border-b-0 ${
          isDanger
            ? 'bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-base)] hover:bg-red-50 dark:hover:bg-red-900/10'
            : 'bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-base)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)]'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDanger ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-slate-100 dark:bg-[var(--color-bg-muted)] text-slate-600 dark:text-slate-400'}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className={`font-medium ${isDanger ? 'text-red-700 dark:text-red-300' : 'text-slate-900 dark:text-slate-200'}`}>{config.title}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 max-w-md">{config.description}</p>
          </div>
        </div>
        <Trash2 className={`w-5 h-5 flex-shrink-0 ${isDanger ? 'text-red-500 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`} />
      </button>
    );
  };

  const confirmConfig = useMemo(() => {
    if (!pendingAction) return null;
    const config = ACTION_CONFIG[pendingAction];
    return {
      title: config.title,
      message: config.confirmMessage,
      onConfirm: () => handleAction(pendingAction),
    };
  }, [pendingAction]);

  return (
    <>
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-8">
        {t('dataSection.title')}
      </h1>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        {t('dataSection.description')}
      </p>

      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200 uppercase tracking-wider mb-4 mt-8">
        {t('dataSection.backupHeading')}
      </h2>
      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50">
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-200">{t('dataSection.exportLabel')}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('dataSection.exportDescription')}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowExportModal(true)}
            disabled={isExporting || isProcessing || isImporting}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
          >
            {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {isExporting ? t('dataSection.exporting') : t('dataSection.export')}
          </button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-base)] border-b-0">
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-200">{t('dataSection.importLabel')}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('dataSection.importDescription')}</p>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleImportData}
              disabled={isImporting || isProcessing || isExporting}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {isImporting ? t('dataSection.importing') : t('dataSection.import')}
            </button>
            {isImporting && importProgress.phase !== 'idle' && (
              <div className="w-full min-w-[200px]">
                <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                  <span>{importProgress.message || t('dataSection.importing')}</span>
                  <span>{t('dataSection.progressPercentPrefix')}{Math.round(importProgress.percent)}</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                  <div className="h-full rounded-full bg-blue-500 transition-all duration-300" style={{ width: `${Math.min(100, Math.max(0, importProgress.percent))}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200 uppercase tracking-wider mb-4 mt-8">
        {t('dataSection.clearDataHeading')}
      </h2>
      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        {renderCard('all')}
        {renderCard('notes')}
        {renderCard('calendar')}
        {renderCard('breaks')}
      </div>

      <DeleteModal
        isOpen={Boolean(pendingAction)}
        onClose={() => (!isProcessing ? setPendingAction(null) : null)}
        onConfirm={() => {
          if (!isProcessing) {
            confirmConfig?.onConfirm();
          }
        }}
        title={confirmConfig?.title}
        message={confirmConfig?.message}
      />

      <ExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} onConfirm={handleExportConfirm} />

      <ImportModal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setPendingImportData(null);
        }}
        onConfirm={handleImportConfirm}
        exportData={pendingImportData}
      />
    </>
  );
};

export default DataSettingsSection;
